import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { CheckboxField } from '../../../../../../../../components/Form/components/CheckboxField/CheckboxField';
import { InputField } from '../../../../../../../../components/Form/components/InputField/InputField';
import { Error } from '../../../../../../../../components/Form/Form.styles';
import { useLogService } from '../../../../../../../../hooks/useLogService';
import { useTranslate } from '../../../../../../../../hooks/useTranslate';
import { useMarket } from '../../../../../../../../providers/MarketProvider';
import { useSembonusUser } from '../../../../../../../../providers/UserProvider';
import {
  performRegister,
  performSubscribeNewsletter,
  type RegisterFormFields,
} from '../../../../../../../../utils/auth';
import {
  ButtonsWrapper,
  FakeLink,
  FieldsWrapper,
  StyledButton,
  StyledForm,
} from '../../AuthModal.styles';
import {
  NameFields,
  StyledError,
  TermsAndConditions,
} from './RegisterForm.styles';

type Props = {
  onSuccess: () => void;
  onLogIn: () => void;
};

export const RegisterForm = ({ onSuccess, onLogIn }: Props) => {
  const { t } = useTranslate();
  const market = useMarket();
  const { checkLoginStatus } = useSembonusUser();
  const { logError } = useLogService();

  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<RegisterFormFields>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      // if we don't show sembonus, it's important that it defaults to false for the signup endpoint
      optedInToSembonus: false,
      //  For a separate request to newsletter endpoint
      optedInNewsletter: false,
    },
  });

  const onSubmit = async (data: RegisterFormFields) => {
    setIsLoading(true);
    setIsError(false);

    try {
      const response = await performRegister(data);
      if (response.ok) {
        if (data.optedInNewsletter) {
          await performSubscribeNewsletter(data);
        }
        await checkLoginStatus();
        return onSuccess();
      }

      setIsError(true);
    } catch (e) {
      setIsError(true);
      logError(e, {
        extra: {
          market,
          location: 'share-widget',
          username: data.email,
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const openLinkInNewTab = (url: string) =>
    window?.open(url, '_blank')?.focus();
  const openRegisterTerms = () => openLinkInNewTab('/our-policies');

  return (
    <StyledForm onSubmit={handleSubmit(onSubmit)}>
      <FieldsWrapper>
        <NameFields>
          <InputField
            autoFocus
            htmlId="share-widget-register-first-name"
            label={t('ui.action.register.firstName.label')}
            placeholder={t('ui.action.register.firstName.placeholder')}
            error={errors.firstName ? true : undefined}
            {...register('firstName', { required: true })}
          />

          <InputField
            htmlId="share-widget-register-last-name"
            label={t('ui.action.register.lastName.label')}
            placeholder={t('ui.action.register.lastName.placeholder')}
            error={errors.lastName ? true : undefined}
            {...register('lastName', { required: true })}
          />
        </NameFields>

        {(errors.lastName || errors.firstName) && (
          <StyledError>
            {t('ui.action.register.name.requiredError')}
          </StyledError>
        )}

        <InputField
          htmlId="share-widget-register-email"
          type="email"
          inputMode="email"
          label={t('ui.action.register.email.label')}
          placeholder={t('ui.action.register.email.placeholder')}
          error={errors.email?.message}
          {...register('email', {
            required: t('ui.action.register.email.requiredError'),
          })}
        />

        <InputField
          htmlId="share-widget-register-password"
          type="password"
          label={t('ui.action.register.password.label')}
          placeholder={t('ui.action.register.password.placeholder')}
          info={t('ui.action.register.password.requirements')}
          error={errors.password?.message}
          {...register('password', {
            required: t('ui.action.register.password.requiredError'),
            minLength: {
              value: 6,
              message: t('ui.action.register.password.minLengthError'),
            },
            maxLength: {
              value: 50,
              message: t('ui.action.register.password.maxLengthError'),
            },
          })}
        />

        <InputField
          htmlId="share-widget-register-confirm-password"
          type="password"
          label={t('ui.action.register.confirmPassword.label')}
          placeholder={t('ui.action.register.confirmPassword.placeholder')}
          error={errors.confirmPassword?.message}
          {...register('confirmPassword', {
            required: t('ui.action.register.confirmPassword.requiredError'),
            validate: (value) =>
              value === getValues('password')
                ? true
                : t('ui.action.register.confirmPassword.noMatchError'),
          })}
        />

        <CheckboxField
          label=""
          htmlId="share-widget-register-newsletter"
          {...register('optedInNewsletter')}
        >
          {t('ui.action.register.newsletter.info')}
        </CheckboxField>

        {isError && <Error>{t('ui.action.register.genericError')}</Error>}
      </FieldsWrapper>

      <ButtonsWrapper>
        <TermsAndConditions>
          {t('ui.action.register.termsAndConditions.info')}

          <FakeLink type="button" onClick={openRegisterTerms}>
            {t('ui.action.register.termsAndConditions.linkLabel')}
          </FakeLink>
        </TermsAndConditions>

        <StyledButton
          variant="primary"
          size="large"
          type="submit"
          disabled={isLoading}
        >
          {isLoading
            ? t('ui.action.register.submitButton.loading')
            : t('ui.action.register.submitButton.label')}
        </StyledButton>

        <StyledButton variant="secondary" size="large" onClick={onLogIn}>
          {t('ui.action.register.logIn')}
        </StyledButton>
      </ButtonsWrapper>
    </StyledForm>
  );
};
